import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { useState, useEffect, useRef } from "react";
import { Messages } from "primereact/messages";
import { Paginator } from "primereact/paginator";
import { useHistory } from "react-router";
import { Panel } from "primereact/panel";
import { BreadCrumb } from "primereact/breadcrumb";
import { Dropdown } from "primereact/dropdown";
import * as constants from "../app_utils/constants/Constants";
import { HOME_ROUTE_PATH } from "../app_utils/route_paths/resolver/PageRoutes";
import { PrimeIcons } from "primereact/api";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import { generalStatusBodyTemplate } from "../app_utils/utils/Utils";
import { paginatorTemplate } from "../app_utils/components/PaginatorTemplate";
import { filtersHeadertemplate } from "../app_utils/components/FiltersPanelHeader";
import { UserSessionUtils } from "../app_utils/utils/UserSessionUtils";

const STATUS_OPTIONS = [
  { label: "All Statuses", value: null },
  { label: "Pending", value: "Pending" },
  { label: "Completed", value: "Completed" },
  { label: "Failed", value: "Failed" },
];

const PaymentReportView = () => {
  const [records, setRecords] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTermFilter, setSearchTermFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [first, setFirst] = useState<number>(0);
  const [limit, setLimit] = useState<number>(constants.MAXIMUM_RECORDS_PER_PAGE);
  const offsetRef = useRef<number>(0);

  const message = useRef<any>();
  const history = useHistory();

  const breadcrumbHome = {
    icon: "pi pi-home",
    command: () => {
      history.push(HOME_ROUTE_PATH);
    },
  };

  const breadcrumbItems = [
    {
      label: `Payment Reports`,
      icon: PrimeIcons.DOLLAR,
    },
  ];

  const getQueryParameters = () => {
    let searchParameters: any = { offset: offsetRef.current, limit: limit };
    if (searchTermFilter !== null) searchParameters.searchTerm = searchTermFilter;
    if (statusFilter !== null) searchParameters.status = statusFilter;
    return searchParameters;
  };

  const fetchRecordsFromServer = () => {
    setIsLoading(true);
    let searchParameters: any = getQueryParameters();

    new BaseApiServiceImpl("/api/v1/payments/ui")
      .getRequestWithJsonResponse(searchParameters)
      .then(async (response) => {
        setIsLoading(false);
        setRecords(response?.records);
        setTotalItems(response?.totalItems);
      })
      .catch((error) => {
        setIsLoading(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  useEffect(() => {
    fetchRecordsFromServer();
  }, []);

  const onSubmitFilter = () => {
    offsetRef.current = 0;
    setFirst(0);
    fetchRecordsFromServer();
  };

  const resetFilters = () => {
    setSearchTermFilter("");
    setStatusFilter(null);
    offsetRef.current = 0;
    setFirst(0);
    fetchRecordsFromServer();
  };

  const rowIndexTemplate = (rowData: any, props: any) => {
    return (
      <React.Fragment>
        <span>{first + (props.rowIndex + 1)}</span>
      </React.Fragment>
    );
  };

  const onPageChange = (e: any) => {
    offsetRef.current = e.page * constants.MAXIMUM_RECORDS_PER_PAGE;
    setFirst(e.first);
    setLimit(constants.MAXIMUM_RECORDS_PER_PAGE);
    fetchRecordsFromServer();
  };

  const filterButtonsTemplate = (
    <>
      <div className="col-6 md:col-2 p-fluid" key="filterBtns">
        <Button
          icon={constants.ICON_SEARCH}
          className={constants.CSS_FILTER_SUBMIT_BUTTON}
          onClick={onSubmitFilter}
          loading={isLoading}
        />
        <Button
          icon={constants.ICON_REFRESH}
          className={constants.CSS_FILTER_RESET_BUTTON}
          onClick={resetFilters}
          loading={isLoading}
        />
      </div>
    </>
  );

  const formatCurrency = (rowData: any) => {
    const amount = rowData?.amount;
    const currency = rowData?.currency || "UGX";
    if (amount == null) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="grid">
      <div className="col-6 flex justify-content-start flex-wrap">
        <BreadCrumb home={breadcrumbHome} model={breadcrumbItems} />
      </div>
      <div className="col-6 flex justify-content-end flex-wrap">
        <Button
          label={"New Payment"}
          icon={PrimeIcons.PLUS}
          className="p-button-secondary p-button-sm"
          onClick={() => history.push("/payment-gateways")}
        />
      </div>
      <Messages ref={message} style={{ width: "100%" }} />
      <div className="col-12">
        <Panel headerTemplate={filtersHeadertemplate} toggleable>
          <div className="grid">
            <div className="col-12 md:col-3">
              <label htmlFor="searchTerm">Search</label>
              <input
                id="searchTerm"
                type="text"
                value={searchTermFilter || ""}
                onChange={(e) => setSearchTermFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSubmitFilter()}
                className="p-inputtext w-full mt-1"
                placeholder="Search by reference, customer, phone..."
              />
            </div>
            <div className="col-12 md:col-3">
              <label htmlFor="statusFilter">Status</label>
              <Dropdown
                id="statusFilter"
                value={statusFilter}
                options={STATUS_OPTIONS}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setStatusFilter(e.value)}
                placeholder="All Statuses"
                className="w-full mt-1"
              />
            </div>
            {filterButtonsTemplate}
          </div>
        </Panel>
      </div>
      <div className="col-12 card">
        <DataTable
          value={records}
          paginator={false}
          className="datatable-responsive"
          paginatorPosition="both"
          emptyMessage="No record found."
          loading={isLoading}
        >
          <Column
            field="Index"
            header="#"
            style={{ width: "70px" }}
            body={rowIndexTemplate}
          ></Column>
          <Column field="externalReference" header="Reference" sortable />
          <Column field="transactionType" header="Type" sortable />
          <Column field="customerName" header="Customer" sortable />
          <Column field="narration" header="Narration" />
          <Column field="msisdn" header="Phone Number" sortable />
          <Column field="network" header="Network" sortable />
          <Column
            header="Amount"
            body={(rowData: any) => formatCurrency(rowData)}
            sortable
          />
          <Column field="status" header="Status" sortable body={(rowData: any) => generalStatusBodyTemplate(rowData?.status)} />
          <Column field="dateCreated" header="Date" sortable />
          {UserSessionUtils.isSuperAdmin() && (
            <Column field="callbackReceived" header="Callback" sortable body={(rowData: any) => rowData?.callbackReceived ? "Yes" : "No"} />
          )}
        </DataTable>

        <Paginator
          first={first}
          rows={constants.MAXIMUM_RECORDS_PER_PAGE}
          totalRecords={totalItems}
          alwaysShow={true}
          onPageChange={onPageChange}
          template={paginatorTemplate}
        />
      </div>
    </div>
  );
};

export default PaymentReportView;
